import React, { useState, useEffect, useCallback } from 'react';
import {
    Search,
    Mail,
    AlertCircle,
    Loader2,
    RotateCw,
    X
} from 'lucide-react';
import { apiClient, NotificationLog } from '../services/api';

interface NotificationLogsProps {
    user: any;
    refreshKey?: number;
}

const NotificationLogs: React.FC<NotificationLogsProps> = ({ user, refreshKey }) => {
    const [logs, setLogs] = useState<NotificationLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [retryingId, setRetryingId] = useState<string | null>(null);

    // Filter states
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 15;

    const loadLogs = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await apiClient.getNotificationLogs({
                page: currentPage,
                limit: pageSize,
                type: typeFilter !== 'all' ? typeFilter : undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined,
            });

            setLogs(response.data);
            setTotalPages(response.pagination.totalPages);
            setTotalCount(response.pagination.total);
        } catch (err: any) {
            setError(err.message || 'Failed to load notification logs');
            console.error('Error loading logs:', err);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, typeFilter, statusFilter]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    useEffect(() => {
        if (refreshKey) loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    const handleRetry = async (logId: string) => {
        try {
            setRetryingId(logId);
            await apiClient.retryNotification(logId);
            await loadLogs();
        } catch (err: any) {
            setError(err.message || 'Failed to retry notification');
        } finally {
            setRetryingId(null);
        }
    };

    const getStatusBar = (status: string): { barClass: string; label: string } => {
        switch (status) {
            case 'delivered': return { barClass: 'bg-green-500',  label: 'Delivered' };
            case 'sent':      return { barClass: 'bg-blue-500',   label: 'Sent' };
            case 'pending':   return { barClass: 'bg-amber-400',  label: 'Pending' };
            case 'failed':    return { barClass: 'bg-red-500',    label: 'Failed' };
            case 'bounced':   return { barClass: 'bg-orange-400', label: 'Bounced' };
            default:          return { barClass: 'bg-gray-300',   label: status };
        }
    };

    const getTypeLabel = (type: string) => {
        switch (type) {
            case 'licence_expiry': return 'Licence Expiry';
            case 'consent_expiry': return 'Consent Expiry';
            case 'compliance_alert': return 'Compliance Alert';
            case 'bulk_operation': return 'Bulk Operation';
            case 'daily_digest': return 'Daily Digest';
            case 'dbs_alert': return 'DBS Alert';
            default: return type;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredLogs = searchTerm
        ? logs.filter(log =>
            log.recipient.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.subject.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : logs;

    return (
        <div>
            {/* Filters and Search */}
            <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-4 mb-5">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Search */}
                    <div className="md:col-span-2 relative">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by recipient or subject..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                        />
                    </div>

                    {/* Type Filter */}
                    <div>
                        <select
                            value={typeFilter}
                            onChange={(e) => {
                                setTypeFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                        >
                            <option value="all">All Types</option>
                            <option value="licence_expiry">Licence Expiry</option>
                            <option value="consent_expiry">Consent Expiry</option>
                            <option value="compliance_alert">Compliance Alert</option>
                            <option value="bulk_operation">Bulk Operation</option>
                            <option value="daily_digest">Daily Digest</option>
                            <option value="dbs_alert">DBS Alert</option>
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                        >
                            <option value="all">All Status</option>
                            <option value="delivered">Delivered</option>
                            <option value="sent">Sent</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                            <option value="bounced">Bounced</option>
                        </select>
                    </div>
                </div>

            </div>

            {/* Filter chips + count + refresh */}
            {(() => {
                const TYPE_LABELS: Record<string, string> = {
                    licence_expiry: 'Licence Expiry',
                    consent_expiry: 'Consent Expiry',
                    compliance_alert: 'Compliance Alert',
                    bulk_operation: 'Bulk Operation',
                    daily_digest: 'Daily Digest',
                    dbs_alert: 'DBS Alert',
                };
                const STATUS_LABELS: Record<string, string> = {
                    delivered: 'Delivered',
                    sent: 'Sent',
                    pending: 'Pending',
                    failed: 'Failed',
                    bounced: 'Bounced',
                };

                const chips: { key: string; label: string; onRemove: () => void }[] = [];

                if (searchTerm) chips.push({
                    key: 'search',
                    label: `Search: "${searchTerm}"`,
                    onRemove: () => { setSearchTerm(''); },
                });
                if (typeFilter !== 'all') chips.push({
                    key: 'type',
                    label: `Type: ${TYPE_LABELS[typeFilter] || typeFilter}`,
                    onRemove: () => { setTypeFilter('all'); setCurrentPage(1); },
                });
                if (statusFilter !== 'all') chips.push({
                    key: 'status',
                    label: `Status: ${STATUS_LABELS[statusFilter] || statusFilter}`,
                    onRemove: () => { setStatusFilter('all'); setCurrentPage(1); },
                });

                const clearAll = () => {
                    setSearchTerm('');
                    setTypeFilter('all');
                    setStatusFilter('all');
                    setCurrentPage(1);
                };

                return (
                    <div className="mb-4 flex justify-between items-start">
                        <div className="flex flex-wrap items-center gap-2">
                            {chips.length > 0 && (
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider mr-1">Filters</span>
                            )}
                            {chips.map(chip => (
                                <span key={chip.key} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white py-1 pl-2.5 pr-1.5 text-sm font-medium text-gray-700 shadow-[0_1px_1px_rgba(15,23,42,0.03)]">
                                    {chip.label}
                                    <button
                                        onClick={chip.onRemove}
                                        className="relative ml-0.5 rounded-full p-1 text-gray-500 transition-colors before:absolute before:-inset-1.5 hover:bg-[#69BE28]/10 hover:text-[#4a8a1c] focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-1"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </span>
                            ))}
                            {chips.length > 1 && (
                                <button onClick={clearAll} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
                                    Clear all
                                </button>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 flex-shrink-0 ml-4 pt-1">
                            {`${totalCount} ${totalCount === 1 ? 'notification' : 'notifications'}`}
                        </p>
                    </div>
                );
            })()}

            {/* Error Display */}
            {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                </div>
            )}

            {/* Logs Table */}
            <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] overflow-hidden">
                {isLoading ? (
                    <div className="p-8 text-center">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: '#69BE28' }} />
                        <p className="mt-2 text-gray-600">Loading notification history...</p>
                    </div>
                ) : filteredLogs.length === 0 ? (
                    <div className="p-8 text-center">
                        <Mail className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Notifications Found</h3>
                        <p className="text-gray-500">
                            {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
                                ? 'Try adjusting your filters.'
                                : 'Notifications will appear here once configured and sent.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50/95 backdrop-blur-sm">
                                <tr>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                        Recipient
                                    </th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                        Subject
                                    </th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                        Sent At
                                    </th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {filteredLogs.map((log) => {
                                    const statusBar = getStatusBar(log.status);

                                    return (
                                        <tr key={log.id} className="hover:bg-gray-50/60 transition-colors">
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {getTypeLabel(log.notificationType)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                                                    <span className="text-sm text-gray-900">{log.recipient}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <span className="text-sm text-gray-900">{log.subject}</span>
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                    <span className={`h-4 w-[3px] rounded-full ${statusBar.barClass}`} aria-hidden="true" />
                                                    {statusBar.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-500">
                                                {log.sentAt ? formatDate(log.sentAt) : '-'}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap text-sm">
                                                {(log.status === 'failed' || log.status === 'bounced') && (
                                                    <button
                                                        onClick={() => handleRetry(log.id)}
                                                        disabled={retryingId === log.id}
                                                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 transition-colors hover:text-gray-800 disabled:opacity-50"
                                                        title="Retry"
                                                    >
                                                        {retryingId === log.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <RotateCw className="w-4 h-4" />
                                                        )}
                                                        {retryingId === log.id ? 'Retrying...' : 'Retry'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationLogs;